import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import axios from 'src/utilities/http-client'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import BuildIcon from '@mui/icons-material/Build'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles } from 'src/v3/settings/settings.slice'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import {
  createBook,
  fetchBooks,
  newlyCreatedBookIDCleared,
  selectBooks,
  selectBooksStatus,
  selectCreateBookStatus,
  selectNewlyCreatedBookID,
} from '../codex.slice'
import type { Book } from '../types'

type SchemaBackfillResponse = {
  schemaVersion: number
  processed: number
  remaining: number
}

let BooksPage = (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let books = useAppSelector(selectBooks)
  let booksStatus = useAppSelector(selectBooksStatus)
  let createBookStatus = useAppSelector(selectCreateBookStatus)
  let newlyCreatedBookID = useAppSelector(selectNewlyCreatedBookID)
  let roles = useAppSelector(selectRoles)
  let isDeveloper = roles.includes('developer')

  let [isMigrating, setIsMigrating] = useState(false)
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  useEffect(() => {
    dispatch(fetchBooks())
  }, [dispatch])

  useEffect(() => {
    if (newlyCreatedBookID) {
      navigate(`/codex/books/${newlyCreatedBookID}`)
      dispatch(newlyCreatedBookIDCleared())
    }
  }, [newlyCreatedBookID, navigate, dispatch])

  let handleNewBook = () => {
    dispatch(
      createBook({
        title: '(Untitled)',
      }),
    )
  }

  let handleEditBook = (book: Book) => {
    navigate(`/codex/books/${book.id}`)
  }

  let handleMigrateSchema = async () => {
    setIsMigrating(true)
    try {
      let response = await axios.post<SchemaBackfillResponse>(
        '/api/codex/schema/backfill',
      )
      let { processed, remaining, schemaVersion } = response.data

      showSuccess(
        remaining > 0
          ? `Migrated ${processed} chapters to schema v${schemaVersion}. ${remaining} remaining — click again to continue.`
          : `Migration complete. ${processed} chapters migrated to schema v${schemaVersion}.`,
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Schema migration failed:', error)
      showError('Failed to migrate content schema')
    } finally {
      setIsMigrating(false)
    }
  }

  let isCreating = createBookStatus === 'pending'
  let isLoading = booksStatus === 'pending'

  return (
    <Container maxWidth="xl" className="py-6">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Books
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isDeveloper && (
            <Button
              variant="outlined"
              startIcon={
                isMigrating ? <CircularProgress size={20} /> : <BuildIcon />
              }
              onClick={handleMigrateSchema}
              disabled={isMigrating}
            >
              Migrate Content Schema
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={
              isCreating ? <CircularProgress size={20} /> : <AddIcon />
            }
            onClick={handleNewBook}
            disabled={isCreating}
          >
            New Book
          </Button>
        </Box>
      </Box>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {books.length === 0 && !isLoading && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No books yet. Create your first book to get started.
            </Typography>
          </Box>
        )}

        {books.map((book) => (
          <Paper
            key={book.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              cursor: 'pointer',
              borderRadius: 0,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover',
              },
              '&:first-of-type': {
                borderTopLeftRadius: 1,
                borderTopRightRadius: 1,
              },
              '&:last-of-type': {
                borderBottomLeftRadius: 1,
                borderBottomRightRadius: 1,
                borderBottom: 'none',
              },
            }}
            onClick={() => handleEditBook(book)}
          >
            <Box
              sx={{
                width: 80,
                height: 120,
                bgcolor: 'background.default',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {book.thumbnails ? (
                <img
                  src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${book.thumbnails}`}
                  alt={book.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 4,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'action.selected',
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {book.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {book.updatedTime &&
                  dayjs(book.updatedTime).format('DD MMM YYYY')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color:
                    book.publicationStatus === 'published'
                      ? 'success.main'
                      : 'error.main',
                  fontWeight: 'medium',
                  textTransform: 'capitalize',
                }}
              >
                {book.publicationStatus}
              </Typography>
            </Box>

            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                handleEditBook(book)
              }}
            >
              <EditIcon />
            </IconButton>
          </Paper>
        ))}
      </Box>
      {NotificationComponent}
    </Container>
  )
}

export default BooksPage
